import Decimal from "decimal.js";
import { validateDecimal } from "./validation";
import { DECIMAL_ONE_HUNDRED, DECIMAL_ZERO } from "./decimal";

export const getDatetimeStringArray = (dt) => ({
  year: dt.getFullYear().toString(),
  month: (dt.getMonth() + 1).toString(),
  date: dt.getDate().toString(),
  day: dayWeekToString(dt.getDay()),
  hour: dt.getHours().toString(),
  minute: dt.getMinutes().toString(),
  second: dt.getSeconds().toString(),
});

export const getDatetimeString = (dt) => {
  return (
    dt.getFullYear().toString() +
    (dt.getMonth() + 1).toString().padStart(2, "0") +
    dt.getDate().toString().padStart(2, "0") +
    dt.getHours().toString().padStart(2, "0") +
    dt.getMinutes().toString().padStart(2, "0") +
    dt.getSeconds().toString().padStart(2, "0")
  );
};

export const getDateString = (dt) => {
  return (
    dt.getFullYear().toString() +
    (dt.getMonth() + 1).toString().padStart(2, "0") +
    dt.getDate().toString().padStart(2, "0")
  );
};

export const getHyphenedDate = (dt = new Date()) => {
  return (
    dt.getFullYear().toString() +
    "-" +
    (dt.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    dt.getDate().toString().padStart(2, "0")
  );
};

export const getSlashedDate = (dt) => {
  return (
    dt.getFullYear().toString() +
    "/" +
    (dt.getMonth() + 1).toString().padStart(2, "0") +
    "/" +
    dt.getDate().toString().padStart(2, "0")
  );
};

export const getYearMonthDateDayDisplay = (dt) => {
  return (
    dt.getFullYear().toString() +
    "年" +
    (dt.getMonth() + 1).toString() +
    "月" +
    dt.getDate().toString() +
    "日 (" +
    dayWeekToString(dt.getDay()) +
    ")"
  );
};

export const getYearToSecondDisplay = (dt) => {
  return (
    dt.getFullYear().toString() +
    "年" +
    (dt.getMonth() + 1).toString() +
    "月" +
    dt.getDate().toString() +
    "日 (" +
    dayWeekToString(dt.getDay()) +
    ") " +
    padZero(dt.getHours()) +
    ":" +
    padZero(dt.getMinutes()) +
    ":" +
    padZero(dt.getSeconds())
  );
};

const padZero = (num) => {
  return num.toString().padStart(2, "0");
};

export const getYearMonthDateDisplay = (dt) => {
  return (
    dt.getFullYear().toString() +
    "年" +
    (dt.getMonth() + 1).toString() +
    "月" +
    dt.getDate().toString() +
    "日"
  );
};

export const getMonthDateDayDisplay = (dt) => {
  return (
    (dt.getMonth() + 1).toString() +
    "月" +
    dt.getDate().toString() +
    "日 (" +
    dayWeekToString(dt.getDay()) +
    ")"
  );
};

export const getMonthDateDisplay = (dt) => {
  return (dt.getMonth() + 1).toString() + "月" + dt.getDate().toString() + "日";
};

export const getDateAfter = (dt, days) => {
  let resDate = new Date(dt);
  resDate.setDate(resDate.getDate() + days);
  return resDate;
};

/** 日付が過去であるかを取得する */
export const isPastDate = (dt) => {
  return getHyphenedDate(dt) < getHyphenedDate();
};

export const dayWeekToString = (day_week) =>
  ["日", "月", "火", "水", "木", "金", "土"][day_week % 7];

// カタカナをひらがなに変換
export const toHiragana = (str) => {
  return str.replace(/[\u30a1-\u30f6]/g, function (match) {
    var chr = match.charCodeAt(0) - 0x60;
    return String.fromCharCode(chr);
  });
};

// 全角数字を半角に変換
export const digitToHankaku = (str) => {
  return str.replace(/[\uff10-\uff19]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xfee0)
  );
};

//  入力された数値をintegerに変換
export const inputValueToInt = (value) => {
  let valueWithoutComma = digitToHankaku(value.replace(/[,\u002c]/g, ""));
  return valueWithoutComma.length > 0 ? parseInt(valueWithoutComma) : 0;
};

//  入力された数値をintegerもしくはnullに変換
export const inputValueToIntOrNull = (value) => {
  let valueWithoutComma = digitToHankaku(value.replace(/[,\u002c]/g, ""));
  return valueWithoutComma.length > 0 ? parseInt(valueWithoutComma) : null;
};

//  入力された数値をdecimalに変換
export const inputValueToDecimal = (value) => {
  let valueWithoutComma = digitToHankaku(value.replace(/[,\u002c]/g, ""));
  return validateDecimal(valueWithoutComma)
    ? new Decimal(valueWithoutComma)
    : DECIMAL_ZERO;
};

//  入力された数値をdecimalもしくはNULLに変換
export const inputValueToDecimalOrNull = (value) => {
  let valueWithoutComma = digitToHankaku(value.replace(/[,\u002c]/g, ""));
  return validateDecimal(valueWithoutComma)
    ? new Decimal(valueWithoutComma)
    : null;
};

//  文字列を、nullならば空文字に変換
export const stringToEmptyIfNull = (value) => {
  return value ? value : "";
};

// 郵便番号ハイフン化
export const getHyphenedPostCode = (postCodeStr) => {
  return postCodeStr.length > 3
    ? postCodeStr.slice(0, 3) + "-" + postCodeStr.slice(3, postCodeStr.length)
    : postCodeStr;
};

// 時刻の「時」から「午前/午後」と「○時」を取得する。
export const getAmPmHour = (hour) => {
  return {
    amPm: hour < 12 ? "午前" : "午後",
    hour: hour % 12,
  };
};

// 小数(Decimal)を百分率(Decimal)に変換する
export const decimalToPercentDecimal = (decimal) =>
  decimal.mul(DECIMAL_ONE_HUNDRED);

// 百分率(Decimal)を小数(Decimal)に変換する
export const percentDecimalToDecimal = (decimal) =>
  decimal.div(DECIMAL_ONE_HUNDRED);

export const formatNumber = (number) => {
  if (number === 0) {
    return "0";
  }
  if (!number) {
    return "";
  }
  const formattedNumber = number
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedNumber;
};

export function formatCurrency(value) {
  // 値が空文字、null、またはundefinedの場合、空文字を返す
  if (!value && value !== 0) return "";

  // 値を数値に変換
  const number = Number(value);

  // 値が無効な数値の場合、空文字を返す
  if (isNaN(number)) return "";

  // 数値をカンマ区切り形式でフォーマットして返す
  return number.toLocaleString("en-US");
}

export const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const filterAutoComplete = (options, idField, nameField, inputValue) => {
  inputValue = inputValue.trim();
  let result = [];
  const idFilter = options.filter((x) =>
    x[idField].toString().startsWith(inputValue)
  );
  const nameFilter = options.filter((x) => x[nameField].includes(inputValue));
  console.log(idFilter, nameFilter);
  result = [...idFilter, ...nameFilter];
  return result;
};

export function formatPostalCode(postalCode) {
  if (!postalCode) return "";
  let postalCodeStr = String(postalCode);
  if (postalCodeStr.length === 0) {
    return "";
  }
  const firstPart = postalCodeStr.substring(0, 3);
  const lastPart = postalCodeStr.substring(3, 7);
  return `${firstPart}-${lastPart}`;
}

export function formatCommodityOwnerCd(commodityOwnerCd) {
  let result = String(commodityOwnerCd);
  if (result.length === 0) {
    return "";
  }
  const firstPart = result.substring(0, 2);
  const lastPart = result.substring(2, result.length);
  return `${firstPart}-${lastPart}`;
}

export function isToday(dateString) {
  const today = new Date();
  const date = new Date(dateString);

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 *
 * @param {Select対象Id} elementId
 * @param {遅延ミリセカンド} delayTimeMs
 */
export async function selectElementAsync(elementId, delayTimeMs) {
  const selectElement = document.getElementById(elementId);
  if (delayTimeMs) await delay(delayTimeMs);
  if (selectElement) selectElement.select();
}

export let countRerender = 0;
export const increaseCountRerender = () => countRerender++;
export const getCountRerender = () => countRerender;



export function checkValidLength(input, limit) {
  let totalLength = 0;

  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i);

    if (
      (charCode >= 0x0020 && charCode <= 0x007E) || // Latin Hankaku
      (charCode >= 0xFF61 && charCode <= 0xFF9F) // Katakana Hankaku
    ) {
      totalLength += 1;
    } 
    else {
      totalLength += 2;
    }

    if (totalLength > limit) {
      return false;
    }
  }
  return true;
}